import React from 'react'
import classes from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { useKato } from '../../../../contexts/kato.context';
import { BirthCountIcon, DeathCountIcon, EmigrationCountIcon, ImmigrationCountIcon, MenIcon, SaldoMigrationIcon, WomenIcon } from '../../../../assets/icons';
import { Bar, BarChart, CartesianGrid, LabelList, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useShared } from '../../../../contexts/shared.context';
import { IPopulationDetailsStats } from '../../../../types/kato.types';
import { getPopulationDetails } from '../../../../helpers/utils';
import FullscreenClose from './fullscreen-close.component';

const fields: (keyof IPopulationDetailsStats)[] = ['birthCount', 'deathCount', 'naturalCount', 'immigrationCount', 'emigrationCount', 'saldoMigration']
const employmentFields: (keyof IPopulationDetailsStats)[] = ['youthPopulationCount', 'employedPopulationCount', 'hiredPopulationCount', 'selfEmployedPopulationCount', 'unemployedPopulationCount']
const icons: any = {
  birthCount: <BirthCountIcon />,
  deathCount: <DeathCountIcon />,
  immigrationCount: <ImmigrationCountIcon />,
  emigrationCount: <EmigrationCountIcon />,
  saldoMigration: <SaldoMigrationIcon />,
  naturalCount: <ImmigrationCountIcon />
}

const translationKey = 'cards.population-dynamic-card';
const baseClass = 'population-dynamic-fullscreen';

const PopulationDynamicFullscreen = () => {
  const { t } = useTranslation();
  const {
    populationIncreaseDynamicStats,
    populationDetailsStats
  } = useKato().katoGeneralInfo;

  const { selectedInfoYear } = useShared();
  const currentYear = selectedInfoYear;
  if (!populationIncreaseDynamicStats || !populationDetailsStats) return null;

  const {
    populationCount,
    maleCount,
    femaleCount,
    populationEmploymentCount,
    economicallyActivePopulation
  } = populationDetailsStats

  const rawData = populationIncreaseDynamicStats
    .filter((d) => d.value !== null)
    .sort((a, b) => a.year - b.year);

  if (!rawData.length) return null;

  const needsScale = +(rawData.find((d) => d.year === currentYear)?.value ?? 0) > 1000;
  const scaleValue = (v: number) => (needsScale ? +(v / 1000).toFixed(2) : v);

  const data = rawData.map((d) => ({
    year: d.year,
    value: scaleValue(+d.value),
  }));

  const employmentData = employmentFields.map((field) => ({
    name: t(`${translationKey}.${field}`).toUpperCase(),
    value: populationDetailsStats[field] || 0,
  }));

  return (
    <div className={classes[baseClass]}>
      <FullscreenClose /> 
      <div className={classes[`${baseClass}__population`]}>
        <div className={classes[`${baseClass}__population__left`]}>
          <div className={classes[`${baseClass}__population__upper`]}>
            <div className={classes[`${baseClass}__population__total`]}>
              <span className={classes[`${baseClass}__population__total-value`]}>{populationCount?.toLocaleString() || 0}</span>
              <span className={classes[`${baseClass}__population__total-unit`]}>{t('people-short')}</span>
            </div>

            <div className={classes[`${baseClass}__population__genders`]}>
              <div className={classes[`${baseClass}__population__gender`]}>
                <div className={classes[`${baseClass}__population__icon`]}>
                  <MenIcon />
                </div>
                <div className={classes[`${baseClass}__population__info`]}>
                  <span className={classes[`${baseClass}__population__info-value`]}>{maleCount?.toLocaleString() || 0}</span>
                  <span className={classes[`${baseClass}__population__info-label`]}>{t(`${translationKey}.male-count`)}</span>
                </div>
              </div>
              <div className={classes[`${baseClass}__population__gender`]}>
                <div className={classes[`${baseClass}__population__icon`]}>
                  <WomenIcon />
                </div>
                <div className={classes[`${baseClass}__population__info`]}>
                  <span className={classes[`${baseClass}__population__info-value`]}>{femaleCount?.toLocaleString() || 0}</span>
                  <span className={classes[`${baseClass}__population__info-label`]}>{t(`${translationKey}.female-count`)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className={classes[`${baseClass}__population__lower`]}>
            <ResponsiveContainer width="100%" height={'100%'}>
              <LineChart
                data={data}
                margin={{ top: 50, right: 20, left: 20, bottom: 0 }}
              >
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="year"
                  axisLine={false}
                  tickLine={false}
                  tick={({ x, y, payload }) => {
                    const year = payload.value;
                    const isCurrent = year === currentYear;
                    return (
                      <text
                        x={x}
                        y={20}
                        textAnchor="middle"
                        fill={isCurrent ? 'var(--color-primary)' : '#818689'}
                        fontSize="0.75rem"
                        fontWeight={isCurrent ? 700 : 400}
                      >
                        {year}
                      </text>
                    );
                  }}
                />
                <YAxis
                  hide
                  domain={['dataMin', 'dataMax']}
                  type="number"
                  tickLine={false}
                />
                <Tooltip
                  cursor={false}
                  formatter={(value: number) => [value, '']}
                  labelFormatter={(label: number) => `${label}`}
                  contentStyle={{
                    fontSize: '0.75rem',
                    maxWidth: '300px',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    color: '#202224',
                  }}
                />
                <Line
                  type="natural"
                  dataKey="value"
                  stroke="#ccc"
                  dot={({ cx, cy, payload }) => {
                    const { year } = payload;
                    const fill =
                      year < currentYear
                        ? '#92D050'
                        : year === currentYear
                          ? '#118F84'
                          : '#6968FD';
                    const r = year === currentYear ? 6.5 : 5;
                    return <circle cx={cx} cy={cy} r={r} fill={fill} key={year} />;
                  }}
                  strokeWidth={1}
                  isAnimationActive={false}
                  connectNulls
                >
                  <LabelList
                    dataKey="value"
                    position="top"
                    dy={-10}
                    style={{ fontSize: '0.75rem', fill: 'var(--color-primary)', fontWeight: 700 }}
                  />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className={classes[`${baseClass}__population__right`]}>
          {fields.map(item => (
            <div key={item} className={classes[`${baseClass}__item`]}>
              <div className={classes[`${baseClass}__item__left`]}>
                <div className={classes[`${baseClass}__item__icon`]}>{icons[item]}</div>
                <div className={classes[`${baseClass}__item__name`]}>{t(`${translationKey}.${item}`)}</div>
              </div>
              <div className={classes[`${baseClass}__item__right`]}>
                {item === 'naturalCount'
                  ? getPopulationDetails(populationDetailsStats, 'naturalCount')
                  : populationDetailsStats[item as keyof IPopulationDetailsStats]?.toLocaleString() || 0}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={classes[`${baseClass}__unit`]}>
        {t('people-short')}
      </div>
      <div className={classes[`${baseClass}__employment`]}>
        <div className={classes[`${baseClass}__employment__left`]}>
          <div className={classes[`${baseClass}__employment__heading`]}>
            {t(`${translationKey}.economically-active-population`)}
          </div>
          <div className={classes[`${baseClass}__employment__value`]}>
            <span className={classes[`${baseClass}__employment__value-count`]}>
              {populationEmploymentCount?.toLocaleString() || 0}
            </span>
            <span className={classes[`${baseClass}__employment__value-unit`]}>
              {t('people-short')}
            </span>
          </div>

          <div className={classes[`${baseClass}__employment__percent`]}>
            {economicallyActivePopulation?.toLocaleString() || 0}%
          </div>
        </div>
        <div className={classes[`${baseClass}__employment__right`]}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={employmentData}
              layout="vertical"
              margin={{ right: 0, left: 60, }}
            >
              <XAxis type="number" hide />
              <YAxis dataKey="name" type="category" tick={{ fontSize: '0.75rem' }} tickLine={false} />
              <Tooltip formatter={(v: number) => v.toLocaleString()} />
              <Bar dataKey="value" fill="#69B3A2">
                <LabelList dataKey="value" position="right" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

export default PopulationDynamicFullscreen